// Pure scale helpers shared by decision-oriented visualizations. Keeping the
// arithmetic outside React makes bounds, folding, and reconciliation behavior
// deterministic and cheap to test.

export function finiteNumber(value, fallback = 0) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
}

export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, finiteNumber(value, min)));
}

export function pctDelta(previous, current) {
    const base = finiteNumber(previous);
    if (base === 0) return null;
    return (finiteNumber(current) - base) / Math.abs(base);
}

export function linearPercent(value, max) {
    const ceiling = finiteNumber(max);
    if (!(ceiling > 0)) return 0;
    return clamp((finiteNumber(value) / ceiling) * 100, 0, 100);
}

export function divergingScale(values) {
    let negative = 0;
    let positive = 0;
    for (const value of values || []) {
        const number = finiteNumber(value);
        if (number < 0) negative = Math.max(negative, -number);
        else positive = Math.max(positive, number);
    }
    const span = negative + positive;
    return {
        negative,
        positive,
        span,
        zeroPercent: span > 0 ? (negative / span) * 100 : 0,
        widthPercent(value) {
            return span > 0 ? (Math.abs(finiteNumber(value)) / span) * 100 : 0;
        },
    };
}

export function reconcileWaterfall(data, tolerance = 1e-6) {
    const start = finiteNumber(data?.start?.value);
    const end = finiteNumber(data?.end?.value);
    const steps = Array.isArray(data?.steps) ? data.steps : [];
    const stepTotal = steps.reduce((sum, step) => sum + finiteNumber(step?.delta), 0);
    const drift = start + stepTotal - end;
    const limit = Math.max(tolerance, Math.abs(end) * tolerance);
    return { start, end, stepTotal, drift, reconciles: Math.abs(drift) <= limit };
}

export function boundedLimit(value, fallback = 10, ceiling = 50, minimum = 1) {
    return Math.min(ceiling, Math.max(minimum, Math.floor(finiteNumber(value, fallback))));
}

export function boundedRows(rows, maxItems, minimum = 1, ceiling = 50) {
    const cap = boundedLimit(maxItems, minimum, ceiling, minimum);
    return Array.isArray(rows) ? rows.slice(0, cap) : [];
}
